<template>
    <div
        v-if="isTopApproved"
        class="mb-6 rounded-2xl border border-gray-200 dark:border-default bg-white dark:bg-surface-card p-4 sm:p-5"
    >
        <div class="flex items-center">
            <div class="flex flex-1 items-center" role="list" aria-label="Priebeh tvojho mentoringu">
                <template v-for="(step, idx) in processSteps" :key="step.id">
                    <!-- Step dot + label -->
                    <div class="flex flex-col items-center" :class="step.status === 'current' ? 'min-w-[40px] sm:min-w-[56px]' : 'min-w-[36px] sm:min-w-[48px]'">
                        <!-- Dot -->
                        <div
                            v-if="step.status === 'completed'"
                            class="flex h-7 w-7 items-center justify-center rounded-full bg-green-500"
                        >
                            <Icon name="heroicons:check" class="h-4 w-4 text-white" />
                        </div>
                        <div
                            v-else-if="step.status === 'current'"
                            class="flex h-9 w-9 items-center justify-center rounded-full border-[3px] border-purple-500 bg-purple-100 dark:bg-purple-950/40"
                        >
                            <div class="h-3 w-3 rounded-full bg-purple-500" />
                        </div>
                        <div
                            v-else
                            class="flex h-7 w-7 items-center justify-center rounded-full bg-gray-200 dark:bg-subtle"
                        >
                            <div class="h-2.5 w-2.5 rounded-full bg-gray-400 dark:bg-subtle" />
                        </div>
                        <!-- Label -->
                        <span
                            class="mt-1.5 text-center leading-tight"
                            :class="{
                                'text-xs font-semibold text-green-700 dark:text-green-300': step.status === 'completed',
                                'text-sm font-bold text-purple-700 dark:text-purple-300': step.status === 'current',
                                'text-xs font-medium text-gray-400 dark:text-txt-body': step.status === 'upcoming'
                            }"
                        >
                            {{ step.label }}
                        </span>
                    </div>
                    <!-- Connecting line -->
                    <div
                        v-if="idx < processSteps.length - 1"
                        class="mx-1 h-0.5 flex-1"
                        :class="idx < processSteps.findIndex(s => s.status === 'current') ? 'bg-green-400' : 'bg-gray-200 dark:bg-subtle'"
                    />
                </template>
            </div>
        </div>

        <!-- Dynamic CTA strip — copy + action depend on current step + most-advanced internal state -->
        <div
            v-if="ctaConfig"
            class="mt-4 flex flex-col gap-3 border-t border-gray-100 dark:border-default pt-4 sm:flex-row sm:items-center sm:justify-between"
        >
            <div class="min-w-0 flex-1">
                <p class="text-sm font-semibold text-gray-900 dark:text-txt-headings">{{ ctaConfig.title }}</p>

                <!-- Profile fulfillment bar — only on step 2, surfaces the same % the user
                     sees in the sidebar so the 85 % target is no longer surprising. -->
                <div v-if="currentStep === 'profil' && bestReadiness" class="mt-2">
                    <div class="relative h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-subtle sm:max-w-md">
                        <div
                            class="h-full rounded-full transition-all duration-500"
                            :class="bestReadiness.percent >= MATCHING_READY_THRESHOLD ? 'bg-green-500' : 'bg-purple-500'"
                            :style="{ width: Math.min(bestReadiness.percent, 100) + '%' }"
                        />
                        <!-- 85 % target marker -->
                        <div
                            class="absolute top-0 h-full w-px bg-gray-500 dark:bg-[#A3A3A4]"
                            :style="{ left: MATCHING_READY_THRESHOLD + '%' }"
                            aria-hidden="true"
                        />
                    </div>
                    <p class="mt-1 text-xs text-gray-500 dark:text-txt-body">
                        <span class="font-semibold text-gray-700 dark:text-txt-body">{{ bestReadiness.percent }} %</span>
                        z {{ MATCHING_READY_THRESHOLD }} % potrebných pre matching
                    </p>
                </div>

                <p v-if="ctaConfig.subtitle" class="mt-1 text-xs text-gray-500 dark:text-txt-body">{{ ctaConfig.subtitle }}</p>
            </div>
            <div class="flex flex-wrap gap-2">
                <!-- Primary action — a link when it navigates, a scroll button when we're already there -->
                <NuxtLink
                    v-if="ctaConfig.primary?.to"
                    :to="ctaConfig.primary.to"
                    class="inline-flex items-center gap-1.5 rounded-lg bg-purple-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-purple-700"
                >
                    <Icon v-if="ctaConfig.primary.icon" :name="ctaConfig.primary.icon" class="h-4 w-4" />
                    {{ ctaConfig.primary.label }}
                </NuxtLink>
                <button
                    v-if="!ctaConfig.primary?.to && ctaConfig.primary?.scrollToId"
                    type="button"
                    class="inline-flex items-center gap-1.5 rounded-lg bg-purple-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-purple-700"
                    @click="handleCtaScroll(ctaConfig.primary.scrollToId)"
                >
                    <Icon v-if="ctaConfig.primary.icon" :name="ctaConfig.primary.icon" class="h-4 w-4" />
                    {{ ctaConfig.primary.label }}
                </button>
                <!-- Secondary action (only used on step 3 Ready where both mentor/mentee finders may show) -->
                <NuxtLink
                    v-if="ctaConfig.secondary"
                    :to="ctaConfig.secondary.to"
                    class="inline-flex items-center gap-1.5 rounded-lg bg-[#047274] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#035f60]"
                >
                    <Icon v-if="ctaConfig.secondary.icon" :name="ctaConfig.secondary.icon" class="h-4 w-4" />
                    {{ ctaConfig.secondary.label }}
                </NuxtLink>
            </div>
        </div>

        <!-- Contribution rows — part of the timeline card, below the CTA strip -->
        <TopPaymentTile :variant="currentStep === 'mentoring' ? 'prominent' : 'muted'" />
    </div>
</template>

<script setup lang="ts">
import type { UserProfile } from '#shared/types/User'
import TopPaymentTile from '@/components/mentoring/TopPaymentTile.vue'
import {
    calculateSectionCompletion,
    getMenteeSections,
    getMentorSections
} from '@/composables/profile/useMentoringProfileCompletion'

const MATCHING_READY_THRESHOLD = 85

const props = withDefaults(defineProps<{
    topMatchedCount?: number
    topAcceptedCount?: number
    topInDiscussionCount?: number
    topRequestedCount?: number
    /** Base path for the step-3 "Otvoriť správy" CTA */
    activityListPath?: string
    /**
     * When provided, used as the path for step 2 "Doplniť profil" CTA (routes to the role with < 85% readiness).
     * Left empty on the role wizard pages — `resolvedProfileEditPath` works it out from the route instead.
     */
    profileEditPath?: string
}>(), {
    topMatchedCount: 0,
    topAcceptedCount: 0,
    topInDiscussionCount: 0,
    topRequestedCount: 0,
    activityListPath: '/profile/activity',
    profileEditPath: ''
})

const MENTOR_WIZARD_PATH = '/profile/mentoring/mentor'
const MENTEE_WIZARD_PATH = '/profile/mentoring/mentee'
const MENTORING_HUB_PATH = '/profile/mentoring'

/** Id of the wizard wrapper on the role pages — the step 2 CTA scrolls to it when already there */
const PROFILE_WIZARD_ANCHOR_ID = 'profile-wizard'

const route = useRoute()
const store = useApplicationStore()
const userProfile = computed<UserProfile | null>(() => store.userProfile as UserProfile | null)

const isTopApproved = computed(
    () => userProfile.value?.mentorTopApproved || userProfile.value?.menteeTopApproved
)

// Use the SAME section-based calculation that the page sidebar shows the user.
// Previously we used calculateMatchingReadiness (different fields + weights) which
// silently disagreed with the sidebar — user saw 5/6 sections green ≈ 89 %, stepper
// gate saw 83 %, banner stayed locked. Aligning the two removes the surprise.
type RoleReadiness = {
    role: 'mentor' | 'mentee'
    percent: number
    missingSections: string[]
}

const mentorReadiness = computed<RoleReadiness | null>(() => {
    const profile = userProfile.value
    if (!profile?.mentorTopApproved) return null
    const sections = getMentorSections(profile, profile.mentorProfile)
    return {
        role: 'mentor',
        percent: calculateSectionCompletion(sections),
        missingSections: sections.filter((s) => !s.completed).map((s) => s.label)
    }
})
const menteeReadiness = computed<RoleReadiness | null>(() => {
    const profile = userProfile.value
    if (!profile?.menteeTopApproved) return null
    const sections = getMenteeSections(profile, profile.menteeProfile)
    return {
        role: 'mentee',
        percent: calculateSectionCompletion(sections),
        missingSections: sections.filter((s) => !s.completed).map((s) => s.label)
    }
})
const isMentorMatchingReady = computed(
    () => (mentorReadiness.value?.percent ?? 0) >= MATCHING_READY_THRESHOLD
)
const isMenteeMatchingReady = computed(
    () => (menteeReadiness.value?.percent ?? 0) >= MATCHING_READY_THRESHOLD
)

// Highest-readiness role drives the "Doplň svoj profil" banner copy + progress bar so
// the user sees exactly where they are vs the 85 % target and what sections are still missing.
const bestReadiness = computed<RoleReadiness | null>(() => {
    const candidates = [mentorReadiness.value, menteeReadiness.value].filter(
        (r): r is NonNullable<typeof r> => r != null
    )
    if (candidates.length === 0) return null
    return candidates.reduce((a, b) => (b.percent > a.percent ? b : a))
})

// Which role wizard the user is currently sitting on (null anywhere else)
const roleFromRoute = computed((): 'mentor' | 'mentee' | null => {
    if (route.path.startsWith(MENTOR_WIZARD_PATH)) return 'mentor'
    if (route.path.startsWith(MENTEE_WIZARD_PATH)) return 'mentee'
    return null
})

// Where "Doplniť profil" should actually take the user.
//
// The role pages render the stepper without `profileEditPath`, and the old default
// was the hub — so the CTA went wizard → hub, and the hub sent the user to the *other*
// role's wizard (mentee is checked first, and the invite link approves BOTH roles),
// whose CTA pointed at the hub again. That was the loop.
const resolvedProfileEditPath = computed((): string => {
    // The hub computes this from live completion data — always honour it
    if (props.profileEditPath) return props.profileEditPath

    // On a role wizard, stay on that role rather than pushing the user to the other one
    if (roleFromRoute.value === 'mentor' && mentorReadiness.value && !isMentorMatchingReady.value) {
        return MENTOR_WIZARD_PATH
    }
    if (roleFromRoute.value === 'mentee' && menteeReadiness.value && !isMenteeMatchingReady.value) {
        return MENTEE_WIZARD_PATH
    }

    if (menteeReadiness.value && !isMenteeMatchingReady.value) return MENTEE_WIZARD_PATH
    if (mentorReadiness.value && !isMentorMatchingReady.value) return MENTOR_WIZARD_PATH

    return MENTORING_HUB_PATH
})

const handleCtaScroll = (elementId: string) => {
    document.getElementById(elementId)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

// Stepper advances to step 3 (Matching) when at least one approved role is ready
const isMatchingReady = computed(() => isMentorMatchingReady.value || isMenteeMatchingReady.value)

// 4-step flow: prihlaska → profil → matching → mentoring
// Step 4 (mentoring) advances the moment either party confirms start
// (status Accepted alone does NOT advance — it stays on step 3 with "Potvrď začiatok" CTA)
const currentStep = computed(() => {
    if (props.topMatchedCount > 0) return 'mentoring'
    if (isMatchingReady.value) return 'matching'
    return 'profil'
})

const processSteps = computed(() => {
    const stepIds = ['prihlaska', 'profil', 'matching', 'mentoring'] as const
    const labels: Record<(typeof stepIds)[number], string> = {
        prihlaska: 'Prihláška',
        profil: 'Profil',
        matching: 'Matching',
        mentoring: 'Mentoring'
    }
    const currentIdx = stepIds.indexOf(currentStep.value as (typeof stepIds)[number])
    return stepIds.map((id, idx) => ({
        id,
        label: labels[id],
        status: idx < currentIdx ? 'completed' : idx === currentIdx ? 'current' : 'upcoming'
    }))
})

// Dynamic CTA strip — picks copy based on currentStep and (on step 3) the most-advanced internal state.
// Internal state priority (only consulted when currentStep === 'matching'):
//   Accepted (Potvrď začiatok) > InDiscussion > Requested > Ready
type CtaAction = { label: string; to?: string; scrollToId?: string; icon?: string }
type CtaConfig = { title: string; subtitle?: string; primary?: CtaAction; secondary?: CtaAction }

const ctaConfig = computed<CtaConfig | null>(() => {
    // Step 4 — Mentoring: intentionally NO generic CTA. The concrete cards rendered below
    // the stepper (running mentorings, awaiting-partner, actionable) carry the actions, so a
    // "Tvoj mentoring beží! → Otvoriť mentoring" banner here was just redundant noise. The
    // timeline dot alone signals the stage.
    if (currentStep.value === 'mentoring') {
        return null
    }

    // Step 3 — Matching: pick the most-advanced internal state
    if (currentStep.value === 'matching') {
        // Accepted but not yet started — intentionally NO generic CTA here. TopActionCards
        // render one card per accepted pair right below the stepper, each with the partner's
        // name, a deep-link to that specific mentoring, and both Potvrď/Odmietnuť actions.
        // A single summary banner couldn't name a partner or link to one pair (it fell back
        // to the list whenever there was more than one), so it was just confusing noise.
        if (props.topAcceptedCount > 0) {
            return null
        }
        // In discussion — passive nudge to keep talking
        if (props.topInDiscussionCount > 0) {
            return {
                title: 'Diskutuješ o spolupráci',
                subtitle: 'Otvor správy a dohodni si podmienky mentoringu.',
                primary: {
                    label: 'Otvoriť správy',
                    to: `${props.activityListPath}?tab=confirm`,
                    icon: 'heroicons:chat-bubble-left-right'
                }
            }
        }
        // Requested — fully passive (no CTA, just info)
        if (props.topRequestedCount > 0) {
            return {
                title: 'Tvoja žiadosť čaká na odpoveď',
                subtitle: 'Dáme ti vedieť, len čo druhá strana zareaguje.'
            }
        }
        // Ready — no requests/discussions yet, show finder CTAs (mentor + mentee if both).
        //
        // The invite link approves BOTH roles, so a mentor who finishes their profile
        // would otherwise be sent to "Nájdi mentora" first. On a role wizard page the
        // order follows that role: finishing the mentor profile leads to "Nájdi mentee".
        const findMentor: CtaAction = { label: 'Nájdi mentora', to: '/mentori/ai-search', icon: 'heroicons:sparkles' }
        const findMentee: CtaAction = { label: 'Nájdi mentee', to: '/mentees/ai-search', icon: 'heroicons:sparkles' }

        const actions: CtaAction[] = []
        if (roleFromRoute.value === 'mentor') {
            if (isMentorMatchingReady.value) actions.push(findMentee)
            if (isMenteeMatchingReady.value) actions.push(findMentor)
        } else {
            if (isMenteeMatchingReady.value) actions.push(findMentor)
            if (isMentorMatchingReady.value) actions.push(findMentee)
        }

        return {
            title: 'Tvoj profil je pripravený na matching!',
            subtitle: 'Nájdi partnerov v Mentoringovom networku pre svetové Slovensko.',
            primary: actions[0],
            secondary: actions[1]
        }
    }

    // Step 2 — Profil: nudge to complete profile (progress bar + missing sections rendered in template)
    if (currentStep.value === 'profil') {
        const readiness = bestReadiness.value
        const missingHint = readiness?.missingSections?.slice(0, 3).join(', ')
        const subtitle = missingHint ? `Chýba: ${missingHint}.` : undefined
        const target = resolvedProfileEditPath.value

        // Already on the wizard this CTA points at — navigating there is a no-op, so
        // scroll into the form instead of bouncing the user around the route tree.
        // Only the role wizard pages carry the anchor; anywhere else a scroll button
        // would be a silent no-op, so fall back to linking.
        const isOnTarget = target === route.path && roleFromRoute.value !== null

        const primary: CtaAction = isOnTarget
            ? { label: 'Doplniť profil', scrollToId: PROFILE_WIZARD_ANCHOR_ID, icon: 'heroicons:arrow-down' }
            : { label: 'Doplniť profil', to: target, icon: 'heroicons:arrow-right' }

        return {
            title: 'Doplň svoj profil',
            subtitle,
            primary
        }
    }

    return null
})
</script>
